import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import Loader from '../common/Loader';
import { SUPPORT_EMAIL } from '../Footer';
import '../../assets/styles/SurveyAnswer.css';
import '../../assets/styles/PaymentResult.css';

const PLAN_KEYS = { 1: 'basic', 2: 'pro', 3: 'business' };
const REDIRECT_MS = 6000;

// Vuelta de Stripe tras pagar: confirma la sesion de pago y activa el plan
const SuccessPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [planKey, setPlanKey] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    // Una sola vez aunque React monte dos veces en desarrollo
    if (startedRef.current) return undefined;
    startedRef.current = true;

    const sessionId = searchParams.get('session_id');
    const token = localStorage.getItem('token');
    if (!sessionId || !token) {
      setStatus('error');
      return undefined;
    }

    let timer;
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/update-subscription-permissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sessionId })
        });
        if (!response.ok) throw new Error(`Error ${response.status}`);
        const data = await response.json();
        setPlanKey(PLAN_KEYS[data.has_permissions] || null);
        setStatus('success');
        timer = setTimeout(() => navigate('/dashboard'), REDIRECT_MS);
      } catch (error) {
        console.error('Error al confirmar la suscripción:', error);
        setStatus('error');
      }
    })();
    return () => clearTimeout(timer);
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <main className="tp-answer tp-payment">
        <div className="tp-answer__inner tp-answer__inner--center">
          <Loader overlay caption={t('pay_processing_text')} />
        </div>
      </main>
    );
  }

  const success = status === 'success';
  return (
    <main className="tp-answer tp-payment">
      <div className="tp-answer__inner tp-answer__inner--center">
        <section className="tp-answer__card tp-answer__state" role={success ? 'status' : 'alert'}>
          <span className={`tp-answer__state-icon ${success ? 'is-success' : 'is-error'}`} aria-hidden="true">
            {success ? <CircleCheck size={30} strokeWidth={1.75} /> : <CircleAlert size={30} strokeWidth={1.75} />}
          </span>
          <h1 className="tp-answer__question">{t(success ? 'pay_success_title' : 'pay_error_title')}</h1>
          <p className="tp-answer__hint">
            {success
              ? (planKey ? t('pay_success_text').replace('{plan}', t(`pricing_${planKey}`)) : t('pay_success_text_noplan'))
              : t('pay_error_text')}
          </p>

          {success ? (
            <ul className="tp-payment__list">
              <li>{t('pay_next_1')}</li>
              <li>{t('pay_next_2')}</li>
            </ul>
          ) : (
            <p className="tp-payment__contact">
              {t('pay_contact')} <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
          )}

          <div className="tp-payment__actions">
            <Link to="/dashboard" className="btn btn--primary">{t('pay_go_dashboard')}</Link>
            <Link to="/subscribe" className="btn btn--outline">{t(success ? 'pay_view_plans' : 'pay_try_again')}</Link>
          </div>
          {success && <p className="tp-payment__note">{t('pay_redirect')}</p>}
        </section>
      </div>
    </main>
  );
};

export default SuccessPage;
